/**
 * Utility functions for managing call participants
 */

/**
 * Maximum number of participants allowed in a one-to-one call
 */
export const MAX_PARTICIPANTS = 2;

/**
 * Validates if a new participant should be allowed to join
 * @param {Map} participants - Current participants map
 * @param {string} newParticipantId - ID of the new participant trying to join
 * @param {string} localParticipantId - ID of the local participant
 * @returns {Object} Validation result with status and message
 */
export const validateNewParticipant = (participants, newParticipantId, localParticipantId) => {
  // If we already have max participants, reject new ones
  if (participants.size >= MAX_PARTICIPANTS && newParticipantId !== localParticipantId) {
    return {
      allowed: false,
      reason: "MAX_PARTICIPANTS_REACHED",
      message: "This is a one-to-one call and already has the maximum number of participants."
    };
  }

  // Check for duplicate display names
  const newParticipant = participants.get(newParticipantId);
  if (!newParticipant) {
    return { allowed: true };
  }

  const participantsList = [...participants.keys()].filter(id => id !== newParticipantId);
  
  // Check if this participant has the same name as an existing one
  const duplicateParticipant = participantsList.find(id => {
    const existingParticipant = participants.get(id);
    return existingParticipant.displayName === newParticipant.displayName;
  });

  if (duplicateParticipant) {
    return {
      allowed: false,
      reason: "DUPLICATE_NAME",
      message: `A participant with the name "${newParticipant.displayName}" is already in the call.`,
      duplicateId: duplicateParticipant
    };
  }

  return { allowed: true };
};

/**
 * Selects the appropriate participants for a one-to-one call
 * @param {Array} allParticipants - List of all participants
 * @param {string} localParticipantId - ID of the local participant
 * @returns {Object} Selected local and remote participants
 */
export const selectOneToOneParticipants = (allParticipants, localParticipantId) => {
  // First, ensure we have the local participant
  const localParticipant = allParticipants.find(p => 
    p.id === localParticipantId || p.isLocal
  );
  
  // Then find a remote participant
  const remoteParticipants = allParticipants.filter(p => 
    p.id !== localParticipantId && !p.isLocal
  );
  
  // If we have multiple remote participants with the same name, take the first one
  const uniqueRemoteParticipants = [];
  const seenNames = new Set();
  
  for (const participant of remoteParticipants) {
    if (!seenNames.has(participant.displayName)) {
      uniqueRemoteParticipants.push(participant);
      seenNames.add(participant.displayName);
    }
  }
  
  return {
    localParticipant,
    remoteParticipant: uniqueRemoteParticipants.length > 0 ? uniqueRemoteParticipants[0] : null,
    duplicateParticipants: remoteParticipants.length - uniqueRemoteParticipants.length
  };
};